import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { firestore } from "@/services/firestore";
import {
  CustomerUpdate,
  CustomerUpdateStatusLabels,
  TerminologyOptions,
} from "@/types/v2";
import {
  CustomerUpdateForm,
  CustomerUpdateFormValues,
} from "@/components/CustomerUpdate/CustomerUpdateForm";
import { useStore } from "@/stores/useStore";
import { useServiceAdvisors } from "@/hooks/shop/useServiceAdvisors";
import { useCustomerUpdate } from "@/hooks/useCustomerUpdate";
import {
  CustomerUpdateUsageLocation,
  useCustomerUpdatesAnalytics,
} from "@/hooks/useCustomerUpdatesAnalytics";
import { BreadcrumbPage } from "@/components/Page/BreadcrumbPage";
import { useTerminologyReplacer } from "@/hooks/useTerminologyReplacer";
import { useHeaderContext } from "@/hooks/useHeaderContext";
import { CustomerUpdatesHeader } from "@/components/Page/Header/CustomerUpdatesHeader";
import { AnalyticsLocationProvider } from "@/providers/AnalyticsLocationProvider";

const CustomerUpdatePage = () => {
  const { shopId, customerUpdateId } = useParams();
  const navigate = useNavigate();
  const { terminolize } = useTerminologyReplacer();
  const { setHeaderContent } = useHeaderContext();
  const shop = useStore((s) => s.shop);
  const cards = useStore((s) => s.cards);
  const serviceAdvisors = useServiceAdvisors();
  const [customerUpdate, setCustomerUpdate] = useState<CustomerUpdate | null>(
    null
  );
  const [submitting, setSubmitting] = useState(false);
  const { logSnoozeUsed, logCustomerUpdateCompleted } =
    useCustomerUpdatesAnalytics();

  const card = useMemo(() => {
    if (!customerUpdate || !cards) return null;
    return cards.find((c) => c.id === customerUpdate.cardId);
  }, [customerUpdate, cards]);

  useEffect(() => {
    setHeaderContent(shopId ? <CustomerUpdatesHeader shopId={shopId} /> : null);
    return () => setHeaderContent(null);
  }, [setHeaderContent, shopId]);

  // Use the firestore service to subscribe to the customer update data
  useEffect(() => {
    const subscription = firestore.subscribeDocument<CustomerUpdate>(
      `/shops/${shopId}/customerUpdates/${customerUpdateId}`,
      (data) => {
        if (data) {
          setCustomerUpdate(data);
        } else {
          setCustomerUpdate(null);
        }
      }
    );
    return () => {
      subscription();
    };
  }, [customerUpdateId, shopId]);

  const {
    handleSnoozeUpdate,
    handleSubmit: handleBaseSubmit,
    snoozeTimeMin,
  } = useCustomerUpdate(customerUpdateId);

  const handleSubmit = async (values: CustomerUpdateFormValues) => {
    setSubmitting(true);
    logCustomerUpdateCompleted(
      CustomerUpdateUsageLocation.PAGE,
      values.userId !== customerUpdate?.userId,
      values.timeUntil
    );
    navigate("..", {
      relative: "path",
      replace: true,
      state: { refreshKey: Date.now() },
    });
    await handleBaseSubmit(values);
    setSubmitting(false);
  };

  const handleSnooze = async () => {
    setSubmitting(true);
    logSnoozeUsed(CustomerUpdateUsageLocation.PAGE);
    navigate("..", {
      relative: "path",
      replace: true,
      state: { refreshKey: Date.now() },
    });
    await handleSnoozeUpdate();
    setSubmitting(false);
    return;
  };

  if (!shopId || !customerUpdateId || !customerUpdate) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="w-full max-w-2xl">
          <h1 className="mb-4 text-2xl font-bold">Error</h1>
          <p className="mb-6 text-red-600">
            Invalid shop or customer update ID.
          </p>
        </div>
      </div>
    );
  }

  return (
    <BreadcrumbPage
      breadcrumbs={[
        { label: "Dashboard", href: `/${shopId}` },
        { label: "Customer Updates", href: `/${shopId}/customer-updates` },
        {
          label: `${terminolize("RO", TerminologyOptions.repairOrder, "acronym")} ${customerUpdate?.cardNumber}`,
        },
      ]}
    >
      <div className="mx-auto w-full max-w-2xl">
        <h1 className="mb-4 text-2xl font-bold">
          Current Status:{" "}
          {customerUpdate
            ? CustomerUpdateStatusLabels[customerUpdate.status]
            : "Loading..."}
        </h1>
        <div
          className={
            "rounded-lg border border-muted bg-white p-4 text-gray-600"
          }
        >
          <AnalyticsLocationProvider
            location={CustomerUpdateUsageLocation.PAGE}
            additionalData={{
              cardId: customerUpdate.cardId,
              shopId: customerUpdate.shopId,
              customerUpdateId: customerUpdate.id,
            }}
          >
            <CustomerUpdateForm
              onSubmit={handleSubmit}
              onSnooze={handleSnooze}
              initialValues={customerUpdate}
              shop={shop}
              cardData={card}
              serviceAdvisors={serviceAdvisors}
              submitting={submitting}
              snoozeTime={snoozeTimeMin}
              location={CustomerUpdateUsageLocation.PAGE}
            />
          </AnalyticsLocationProvider>
        </div>
      </div>
    </BreadcrumbPage>
  );
};

export default CustomerUpdatePage;
